import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import EloBadge from "@/components/EloBadge";
import Avatar from "@/components/Avatar";
import PlayerLink from "@/components/PlayerLink";
import MatchmakingOverlay from "@/components/MatchmakingOverlay";
import ResignConfirmModal from "@/components/ResignConfirmModal";
import YourGames from "@/components/YourGames";
import ChallengePlayers from "@/components/ChallengePlayers";
import BoardChoice from "@/components/BoardChoice";
import UnbrokenChainToggle from "@/components/UnbrokenChainToggle";
import SameTilesToggle from "@/components/SameTilesToggle";
import DailyCard from "@/components/DailyCard";
import { CLOCKS } from "@/lib/clocks";
import MiniCard from "@/components/MiniCard";
import GuestBanner from "@/components/GuestBanner";
import NamePoll from "@/components/NamePoll";
import Avatar2D from "@/components/Avatar2D";
import BotFace from "@/components/BotFace";
import { myCharacter } from "@/lib/avatarConfig";
import {
  Lightning,
  Hourglass,
  ArrowRight,
  Clock,
  Trophy,
  Robot,
  PlayCircle,
  Timer,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const LAYOUTS = [
  { id: "random", name: "Random", desc: "Surprise! One of the three layouts.", letter: "?" },
  { id: "classic", name: "Old Classic", desc: "Walls on the flanks, cuts spread wide.", letter: "C" },
  { id: "open_field", name: "Open Field", desc: "No walls. Cuts decide everything.", letter: "O" },
  { id: "fortress", name: "Classic Fortress", desc: "Heavy walls. Cuts essential.", letter: "F" },
  { id: "boardgame5", name: "The Wave", desc: "Taller 33-row board. Gem squares and wedges of wall tiles down both sides.", letter: "W" },
  { id: "bridges", name: "Bridges (old)", desc: "Narrow 13x27 board. Reach your bridge square before crossing the middle. 10 tiles, 5 walls to start.", letter: "B" },
  { id: "bridges_trial", name: "Bridges (short)", desc: "Narrow 13x27 board with a river. Earn bridge tiles on your yellow squares and lay them to cross.", letter: "R" },
  { id: "bridges2", name: "Bridges", desc: "Narrow 13x31 board with a river. Earn bridge tiles on your yellow squares and lay them to cross.", letter: "R" },
  { id: "bridges3", name: "Bridges 2.0", desc: "Bridges with a row of sand each side of the river. Wall tiles go anywhere on your side but the sand.", letter: "R" },
  { id: "mini", name: "Mini", desc: "Compact 12-row board. 10 tiles. Fast.", letter: "M" },
  { id: "boardgame", name: "Original", desc: "Tall 31×15 board. Square bases and POW cards.", letter: "O" },
  { id: "boardgame2", name: "Fortress", desc: "Classic plus gem squares that earn wall tiles.", letter: "F" },
  { id: "fortress2", name: "Fortress 2.0", desc: "Fortress with no grey POWs and 10 tiles. Land on all 3 of your green POWs for 2 more.", letter: "F" },
  { id: "infinity2", name: "Infinity", desc: "Fortress 2.0 with 5 walls in the middle and a wall in each corner. The sides are joined: words run off one edge and on from the other.", letter: "I" },
  { id: "boardgame3", name: "Classic", desc: "The standard board with POW cards and Connector Tiles. No gems.", letter: "C" },
  { id: "firefly2", name: "Firefly", desc: "Taller 33-row board. Gem squares and wall tiles already in front of each base.", letter: "F" },
];

// What the main menu offers for now (Jack, 2026-10-08). The other modes,
// time controls and boards still work; add them back here to show them.
const MENU_MODES = ["turn_based"];
const MENU_TURN_TC = [0];
// "Original" (boardgame) is hidden from the lobby. Classic is the default and
// Fortress the second choice; every game starts with a board pick (Jack, 2026-10-12).
// Firefly and The Wave added 2026-10-14.
const MENU_LAYOUTS = ["boardgame3", "boardgame2", "fortress2", "infinity2", "firefly2", "boardgame5", "bridges2", "bridges3"]; // the earlier Bridges boards (bridges, bridges_trial) are hidden (Jack, 2026-10-27)
const MENU_LAYOUT_LIST = MENU_LAYOUTS.map((id) => LAYOUTS.find((l) => l.id === id));

// Bots play by exactly the same rules as people, and bot games never change
// your Elo (Jack, 2026-10-12). Elo values are what bot-lab/arena.js measured.
// Shown to every player (Jack, 2026-10-12, after testing them himself).
// Set SHOW_BOTS to false to hide them again; testers (server can_play_bots,
// or a device that opened ?bots=1, see index.js) would still see them.
const SHOW_BOTS = true;
// Random matchmaking (closest-Elo queue) is hidden while the player base is
// small; Find opponent opens a list of every player to challenge instead
// (Jack, 2026-10-16). Set true to bring the queue and its board picker back.
const RANDOM_MATCHMAKING = false;
function botsUnlocked(searchParams) {
  try {
    const q = searchParams.get("bots");
    if (q === "1") localStorage.setItem("jangle_bots", "1");
    if (q === "0") localStorage.removeItem("jangle_bots");
    return SHOW_BOTS || localStorage.getItem("jangle_bots") === "1";
  } catch {
    return SHOW_BOTS;
  }
}

const BOTS = [
  { id: "rookie",      name: "Rookie Rufus",       elo:  800, blurb: "Knows everyday words. Plays loose.",         accent: "from-emerald-500/20 to-emerald-500/5", ring: "ring-emerald-500/30" },
  { id: "apprentice",  name: "Apprentice Ada",     elo: 1250, blurb: "Common words, POW cards, a little patience.", accent: "from-sky-500/20 to-sky-500/5",         ring: "ring-sky-500/30" },
  { id: "mid",         name: "Mid Mike",           elo: 1550, blurb: "Races hard. Uses Shield Tiles to block you.",  accent: "from-violet-500/20 to-violet-500/5",   ring: "ring-violet-500/30" },
  { id: "strategist",  name: "Strategist Sam",     elo: 1625, blurb: "Big vocabulary. Thinks about your reply.",     accent: "from-amber-500/20 to-amber-500/5",     ring: "ring-amber-500/30" },
  { id: "grandmaster", name: "Grandmaster Greta",  elo: 1750, blurb: "Knows every word. Looks furthest ahead.",       accent: "from-rose-500/20 to-rose-500/5",       ring: "ring-rose-500/30" },
  // Jack, 2026-10-15: the strongest bot. Thinks for up to 12s a move.
  { id: "magnus",      name: "Magnus",             elo: 2000, blurb: "The strongest. Plans for your whole reply, POWs and walls included.", accent: "from-yellow-500/25 to-yellow-500/5", ring: "ring-yellow-500/40" },
];

// The bot closest to your Elo.
function suggestedBot(elo) {
  return BOTS.reduce((best, b) => (Math.abs(b.elo - elo) < Math.abs(best.elo - elo) ? b : best), BOTS[0]);
}

// Games that sit in "Your games" and never block starting another one:
// untimed turn-based games against people, and bot games (saved after every
// bot turn). An unfinished bot game used to grey out the bot boxes.
function isAsyncMatch(m) {
  return !!m.is_bot || (m.mode === "turn_based" && m.time_control_seconds === 0);
}

function timeAgo(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const d = Date.now() - t;
  const s = Math.floor(d / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function Lobby() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [botsOnDevice] = useState(() => botsUnlocked(searchParams));
  const showBots = botsOnDevice || !!user?.can_play_bots;
  // Optional "unbroken chain" rule for bot games (Jack, 2026-10-14; open to
  // everyone since 2026-10-29): a win needs one unbroken chain of your tiles
  // into the other base.
  const [testUnbroken, setTestUnbroken] = useState(false);
  // Optional "Same tiles" game (Jack, 2026-10-30): the same bag order for both
  // players, the opponent's tiles blank until the end.
  const [sameTiles, setSameTiles] = useState(false);
  // Who moves first in a bot game (Jack, 2026-10-18): you, the bot, or
  // Random. Every bot game starts on Random (Jack, 2026-10-19); the last
  // pick isn't remembered.
  const [firstChoice, setFirstChoice] = useState("random");
  // Bot games (Jack, 2026-11-02): take turns, or play each turn at the same
  // time as the bot ("Same time", as in timed games against people).
  const [botSimul, setBotSimul] = useState(false);
  // Same time against the bot has chess clocks, the same presets as people games.
  const [botClock, setBotClock] = useState("7");
  const [mode, setMode] = useState(
    MENU_MODES.includes(searchParams.get("mode"))
      ? searchParams.get("mode") : MENU_MODES[0]
  );
  const [layout, setLayout] = useState(
    MENU_LAYOUTS.includes(searchParams.get("layout"))
      ? searchParams.get("layout") : MENU_LAYOUTS[0]
  );
  // Time control (seconds). Defaults to 2 min per round (realtime) / 60s per turn (turn-based).
  // We auto-snap to a valid option whenever `mode` changes.
  const REALTIME_TC = [
    { sec: 60,  label: "1m" },
    { sec: 120, label: "2m" },
    { sec: 180, label: "3m" },
  ];
  const TURN_TC = [
    { sec: 30, label: "30s" },
    { sec: 60, label: "1m" },
    { sec: 90, label: "90s" },
    { sec: 0, label: "None" }, // no time limit per turn
  ].filter((tc) => MENU_TURN_TC.includes(tc.sec));
  const [timeControl, setTimeControl] = useState(MENU_MODES[0] === "turn_based" ? TURN_TC[0].sec : 60);
  // If the mode changes, snap to the default for that mode.
  useEffect(() => {
    if (mode === "realtime") setTimeControl((t) => REALTIME_TC.some((x) => x.sec === t) ? t : 60);
    else setTimeControl((t) => TURN_TC.some((x) => x.sec === t) ? t : TURN_TC[0].sec);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);
  const [searching, setSearching] = useState(false);
  const [waited, setWaited] = useState(0);
  const [matches, setMatches] = useState([]);
  const [pendingMatch, setPendingMatch] = useState(null); // active match awaiting resume/resign
  const [resignConfirmOpen, setResignConfirmOpen] = useState(false);
  const [startingBot, setStartingBot] = useState(null); // bot id while creating the match
  // When user clicks a bot, we open a board picker first (lets them choose
  // the layout for that specific match without committing to the default).
  const [botBoardPick, setBotBoardPick] = useState(null); // {id, name, elo, ...} or null
  const [playersOpen, setPlayersOpen] = useState(false);
  // Timed game card (server flag timed_test, everyone now): its own player list.
  const [timedOpen, setTimedOpen] = useState(false);
  const pollRef = useRef(null);
  const tickRef = useRef(null);
  // Unseen abandonment-defeat notification (the user got logged out / closed
  // the tab during a match, and the backend auto-forfeited them after the
  // disconnect grace window. We need to tell them — they're the loser and
  // never saw the Game Over screen because they weren't in /match/{id}).
  const [defeatNotice, setDefeatNotice] = useState(null);

  const me = myCharacter(user);
  const layoutName = LAYOUTS.find((l) => l.id === layout)?.name || "Classic";
  const matchFormat = [
    layoutName,
    mode === "realtime" ? "Real-time" : "Turn-based",
    timeControl ? `${timeControl}s ${mode === "realtime" ? "per round" : "per turn"}` : "No time limit",
  ].join(" · ");

  // Find-a-match is now ALWAYS human queue
  const opponent = "human";

  const loadMatches = async () => {
    try {
      const { data } = await api.get("/me/matches?limit=20");
      // Recent battles: the last five finished games, newest finish first.
      // Open games live in "Your games" and would crowd them out here.
      setMatches(data.filter((m) => m.status === "completed").slice(0, 5));
      // Detect an active match that blocks a new search (resume banner).
      // Untimed turn-based games against people don't block: they live in
      // the "Your games" list and you can have any number of them.
      const active = data.find((m) => m.status === "active" && !isAsyncMatch(m));
      setPendingMatch(active || null);
      // ── Surface ONLY fresh, opponent-driven abandonment defeats ──
      // Conditions for the modal:
      //   • Match completed in the last 2 minutes
      //   • win_type === "abandonment" (NOT "resign" — those are voluntary)
      //   • winner is someone else
      //   • not already acknowledged
      // We deliberately skip resigns: if the player tapped Exit and chose
      // Resign, they consciously ended the match and shouldn't be hit with
      // another pop-up. Stale older losses are also skipped — the player
      // doesn't need to be reminded of a defeat from yesterday.
      try {
        const seenRaw = localStorage.getItem("jangle:defeats_seen") || "[]";
        const seen = new Set(JSON.parse(seenRaw));
        const myId = user?.id;
        const now = Date.now();
        const RECENT_MS = 2 * 60 * 1000; // 2 minutes
        const recent = data.find((m) => {
          if (m.status !== "completed") return false;
          if (m.win_type !== "abandonment") return false;
          if (!m.winner_id || m.winner_id === myId) return false;
          if (seen.has(m.match_id)) return false;
          const completedAt = m.completed_at ? Date.parse(m.completed_at) : 0;
          if (!completedAt || isNaN(completedAt)) return false;
          return now - completedAt <= RECENT_MS;
        });
        if (recent) {
          const isP1 = recent.player1_id === myId;
          const myElo = isP1 ? recent.player1_elo : recent.player2_elo;
          const myNew = isP1 ? recent.player1_new_elo : recent.player2_new_elo;
          const myDelta = (typeof myNew === "number" && typeof myElo === "number")
            ? myNew - myElo
            : (isP1 ? recent.player1_elo_delta : recent.player2_elo_delta) ?? null;
          const oppName = isP1 ? recent.player2_username : recent.player1_username;
          setDefeatNotice({
            matchId: recent.match_id,
            oppName: oppName || "Opponent",
            eloDelta: myDelta,
            idle: isAsyncMatch(recent),
          });
        }
      } catch { /* localStorage errors etc — ignore */ }
    } catch (e) {
      // ignore
    }
  };

  // Mark the current defeat notice as seen and close the modal.
  const dismissDefeatNotice = () => {
    if (!defeatNotice) return;
    try {
      const seenRaw = localStorage.getItem("jangle:defeats_seen") || "[]";
      const seen = JSON.parse(seenRaw);
      if (!seen.includes(defeatNotice.matchId)) {
        seen.push(defeatNotice.matchId);
        // Keep the list bounded (last 50)
        const trimmed = seen.slice(-50);
        localStorage.setItem("jangle:defeats_seen", JSON.stringify(trimmed));
      }
    } catch { /* ignore */ }
    setDefeatNotice(null);
  };

  useEffect(() => {
    loadMatches();
    refreshUser();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, []); // eslint-disable-line

  // "New game" after a bot game lands here with ?pick=bot: show the bots so
  // you can choose the next one (Jack, 2026-10-12).
  const botCardRef = useRef(null);
  const [botCardGlow, setBotCardGlow] = useState(false);
  useEffect(() => {
    if (searchParams.get("pick") !== "bot" || !showBots) return;
    const t = setTimeout(() => {
      botCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      setBotCardGlow(true);
    }, 150);
    const t2 = setTimeout(() => setBotCardGlow(false), 2600);
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, [searchParams, showBots]);

  // Auto-trigger a search when ?autoFind=1 is in the URL (Rematch / New Game
  // flows from the post-match GameOverModal). Pre-fill mode/tc/layout/opp
  // from the URL so "New game" reuses the exact settings of the prior match.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (searchParams.get("autoFind") !== "1") return;
    const urlMode = searchParams.get("mode");
    const urlTc = searchParams.get("tc");
    const urlLayout = searchParams.get("layout");
    const urlOpp = searchParams.get("opp"); // 'human' or 'ai'
    const urlBot = searchParams.get("bot"); // bot id when opp=ai
    if (urlMode === "realtime" || urlMode === "turn_based") setMode(urlMode);
    if (urlTc) {
      const n = Number(urlTc);
      // 0 = "None" (no time limit) — turn-based only.
      if (!Number.isNaN(n) && (n > 0 || (n === 0 && urlMode === "turn_based"))) setTimeControl(n);
    }
    if (MENU_LAYOUTS.includes(urlLayout)) setLayout(urlLayout);
    if (urlOpp === "human" || urlOpp === "ai") setOpponent(urlOpp);
    // Defer two ticks so state updates flush before triggering search.
    const t = setTimeout(() => {
      // For bot replays, open the picker pre-selected to the prior bot's
      // chosen layout so the user just confirms; for humans, start search.
      if (showBots && urlOpp === "ai" && urlBot) {
        const bot = BOTS.find(b => b.id === urlBot);
        if (bot) startBotMatch(bot, { layout: MENU_LAYOUTS.includes(urlLayout) ? urlLayout : MENU_LAYOUTS[0] });
      } else if (RANDOM_MATCHMAKING) {
        startSearch();
      } else {
        setPlayersOpen(true);
      }
    }, 200);
    return () => clearTimeout(t);
  }, []);

  const startSearch = async () => {
    setSearching(true);
    setWaited(0);
    try {
      const { data } = await api.post("/match/find", { mode, opponent, layout, time_control_seconds: timeControl });
      if (data.status === "matched") {
        setSearching(false);
        navigate(`/match/${data.match_id}?layout=${layout}`);
        return;
      }
      if (data.status === "existing") {
        // Don't silently navigate — let user choose Resume or Resign
        setSearching(false);
        setPendingMatch(data.match);
        toast.info("You already have an active match. Resume or resign it first.");
        return;
      }
      // Queued (human-only). Start polling.
      tickRef.current = setInterval(() => setWaited((w) => w + 1), 1000);
      pollRef.current = setInterval(async () => {
        try {
          const { data: s } = await api.get("/match/queue-status");
          if (s.status === "matched") {
            clearInterval(pollRef.current);
            clearInterval(tickRef.current);
            setSearching(false);
            navigate(`/match/${s.match_id}?layout=${layout}`);
          }
        } catch (e) {
          // ignore transient
        }
      }, 2000);
    } catch (e) {
      setSearching(false);
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to queue");
    }
  };

  const cancelSearch = async () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (tickRef.current) clearInterval(tickRef.current);
    try {
      await api.post("/match/cancel");
    } catch (e) {
      // ignore
    }
    setSearching(false);
  };

  const switchToAI = async () => {
    // Cancel the human queue and immediately start a match against the bot
    // closest to your Elo. Players can pick another one from "Play a bot".
    if (pollRef.current) clearInterval(pollRef.current);
    if (tickRef.current) clearInterval(tickRef.current);
    try {
      await api.post("/match/cancel");
    } catch (e) {
      // ignore
    }
    const fallback = suggestedBot(user?.elo_rating || 1200);
    try {
      const { data } = await api.post("/match/find", {
        mode, opponent: "ai", layout, time_control_seconds: timeControl,
        bot_id: fallback.id, bot_elo: fallback.elo, bot_name: fallback.name,
      });
      if (data.status === "matched") {
        setSearching(false);
        navigate(`/match/${data.match_id}?layout=${layout}`);
      } else if (data.status === "existing") {
        setSearching(false);
        setPendingMatch(data.match);
      }
    } catch (e) {
      setSearching(false);
      toast.error("Failed to start AI match");
    }
  };

  const startBotMatch = async (bot, opts = {}) => {
    setStartingBot(bot.id);
    // The bot card opens a "choose your board" modal first — when the user
    // picks a layout we re-enter this function with opts.layout set.
    const chosenLayout = MENU_LAYOUTS.includes(opts.layout) ? opts.layout : layout;
    try {
      const { data } = await api.post("/match/find", {
        mode, opponent: "ai", layout: chosenLayout, time_control_seconds: timeControl,
        bot_id: bot.id, bot_elo: bot.elo, bot_name: bot.name,
        ...(testUnbroken ? { test_rules: "unbroken" } : {}),
        ...(sameTiles ? { same_tiles: true } : {}),
        ...(botSimul ? { simultaneous: true, clock: botClock } : {}),
        first: botSimul ? "me" : firstChoice,
      });
      if (data.status === "matched") {
        navigate(`/match/${data.match_id}?layout=${chosenLayout}`);
        return;
      }
      if (data.status === "existing") {
        setPendingMatch(data.match);
        toast.info("Resume your active match first.");
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to start bot match");
    } finally {
      setStartingBot(null);
      setBotBoardPick(null);
    }
  };

  const resumeMatch = () => {
    if (!pendingMatch) return;
    navigate(`/match/${pendingMatch.match_id}?layout=${pendingMatch.layout || "classic"}`);
  };

  // A live (timed) game against a person that hasn't started: both players
  // were never in it together, so it can be called off with no Elo
  // (Jack, 2026-11-02).
  const pendingWaiting = !!pendingMatch && !!pendingMatch.clock && !pendingMatch.is_bot
    && pendingMatch.layout !== "race" && !pendingMatch.clock_turn_started_ms;
  const callOffPendingMatch = async () => {
    if (!pendingMatch) return;
    try {
      await api.post(`/match/${pendingMatch.match_id}/call-off`);
      toast.success("Game called off. No Elo changes.");
      setPendingMatch(null);
      loadMatches();
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't call off the game");
      loadMatches();
    }
  };

  const resignPendingMatch = async () => {
    if (!pendingMatch) return;
    setResignConfirmOpen(false);
    try {
      await api.post(`/match/${pendingMatch.match_id}/resign`);
      toast.success("Match resigned. Your opponent has been credited the win.");
      setPendingMatch(null);
      loadMatches();
      // Refresh user Elo since the resign updated it.
      try { window.location.reload(); } catch { /* noop */ }
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to resign match");
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="lobby-page">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Active match banner */}
        {pendingMatch && (
          <div
            className="lg:col-span-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-5 rounded-2xl border border-brand/40 bg-brand/10"
            data-testid="active-match-banner"
          >
            <div className="flex items-start gap-3">
              <Trophy size={22} weight="fill" className="text-brand mt-0.5" />
              <div>
                <div className="font-display font-bold tracking-tight">
                  {pendingWaiting
                    ? `Waiting for ${pendingMatch.player1_id === user?.id ? pendingMatch.player2_username : pendingMatch.player1_username} to join`
                    : "You have an unfinished match"}
                </div>
                <div className="text-sm text-muted-foreground">
                  vs {pendingMatch.player1_id === user?.id ? pendingMatch.player2_username : pendingMatch.player1_username}
                  {pendingMatch.is_bot && " · Bot"} · {pendingMatch.mode?.replace("_", "-")} ·{" "}
                  {pendingMatch.layout?.replace("_", " ") || "classic"}
                </div>
              </div>
            </div>
            <div className="flex gap-2 flex-none">
              {pendingWaiting ? (
                <Button
                  onClick={callOffPendingMatch}
                  variant="ghost"
                  className="rounded-full text-muted-foreground hover:text-destructive"
                  data-testid="call-off-pending-match-btn"
                >
                  Call off
                </Button>
              ) : (
                <Button
                  onClick={() => setResignConfirmOpen(true)}
                  variant="ghost"
                  className="rounded-full text-muted-foreground hover:text-destructive"
                  data-testid="resign-pending-match-btn"
                >
                  Resign
                </Button>
              )}
              <Button
                onClick={resumeMatch}
                className="rounded-full bg-brand hover:bg-brand-hover text-brand-foreground"
                data-testid="resume-match-btn"
              >
                {pendingWaiting ? "Rejoin" : "Resume match"} <ArrowRight size={15} weight="bold" className="ml-1" />
              </Button>
            </div>
          </div>
        )}

        <GuestBanner className="lg:col-span-3" />

        {/* Find match card */}
        {/* Your raider + Find a match ("Raider arena" look, Jack 2026-10-12) */}
        <div
          className="lg:col-span-3 border border-border rounded-2xl overflow-hidden"
          style={{ background: "linear-gradient(135deg, hsl(var(--brand) / 0.14), hsl(var(--surface)) 55%)" }}
          data-testid="find-match-card"
        >
          <div className="flex flex-col md:flex-row items-center md:items-end gap-4 md:gap-10 p-6 md:p-10">
          <div className="flex-none flex flex-col items-center">
            <Avatar2D character={me} flag="#f393a1" className="w-40 md:w-56" />
            <Link
              to="/avatar"
              className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground hover:text-brand transition-colors"
              data-testid="change-raider-link"
            >
              Change raider
            </Link>
          </div>
          <div className="flex-1 w-full min-w-0 text-center md:text-left">
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter break-words">{user?.username}</h1>
          <div className="mt-3 mb-6 flex flex-wrap items-center justify-center md:justify-start gap-3">
            <EloBadge elo={user?.elo_rating || 1200} />
            <span className="text-sm text-muted-foreground">
              {user?.matches_played || 0} matches · {user?.wins || 0}W / {user?.losses || 0}L
            </span>
          </div>

          {/* Mode toggle (shown only when there is more than one mode) */}
          {MENU_MODES.length > 1 && (
          <div className="mb-6">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">Mode</p>
            <div className={`grid ${MENU_MODES.length > 1 ? "grid-cols-2" : "grid-cols-1"} gap-3`}>
              {MENU_MODES.includes("realtime") && (<button
                type="button"
                onClick={() => setMode("realtime")}
                className={`text-left p-4 rounded-xl border transition-colors duration-200 ${
                  mode === "realtime" ? "border-brand bg-brand/10" : "border-border bg-background hover:border-foreground/40"
                }`}
                data-testid="mode-realtime-btn"
              >
                <Lightning size={22} weight="fill" className={mode === "realtime" ? "text-brand" : "text-muted-foreground"} />
                <div className="font-display font-bold mt-2">Real-time</div>
                <div className="text-xs text-muted-foreground mt-1">Race the clock. Both players act at once.</div>
              </button>)}
              {MENU_MODES.includes("turn_based") && (<button
                type="button"
                onClick={() => setMode("turn_based")}
                className={`text-left p-4 rounded-xl border transition-colors duration-200 ${
                  mode === "turn_based" ? "border-brand bg-brand/10" : "border-border bg-background hover:border-foreground/40"
                }`}
                data-testid="mode-turnbased-btn"
              >
                <Hourglass size={22} weight="fill" className={mode === "turn_based" ? "text-brand" : "text-muted-foreground"} />
                <div className="font-display font-bold mt-2">Turn-based</div>
                <div className="text-xs text-muted-foreground mt-1">Think slow. Play sharp.</div>
              </button>)}
            </div>
          </div>

          )}

          {/* Time control sub-section — depends on Mode */}
          {(mode === "realtime" ? REALTIME_TC : TURN_TC).length > 1 && (
          <div className="mb-6" data-testid="time-control-section">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-1">Time control</p>
            <p className="text-[11px] text-muted-foreground/80 mb-3" data-testid="time-control-subheading">
              {mode === "realtime" ? "Per round" : "Per turn"}
            </p>
            <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${(mode === "realtime" ? REALTIME_TC : TURN_TC).length}, minmax(0, 1fr))` }}>
              {(mode === "realtime" ? REALTIME_TC : TURN_TC).map((tc) => (
                <button
                  key={tc.sec}
                  type="button"
                  onClick={() => setTimeControl(tc.sec)}
                  className={`text-center py-3 rounded-xl border font-display font-bold tracking-tight transition-colors duration-200 ${
                    timeControl === tc.sec
                      ? "border-brand bg-brand/10 text-brand"
                      : "border-border bg-background hover:border-foreground/40 text-foreground"
                  }`}
                  data-testid={`time-control-${tc.sec}s-btn`}
                >
                  {tc.label}
                </button>
              ))}
            </div>
          </div>

          )}

          {/* Layout picker (random matchmaking only) */}
          {RANDOM_MATCHMAKING && (
          <div className="mb-8">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">Board layout</p>
            <div className={`grid ${MENU_LAYOUT_LIST.length > 1 ? "grid-cols-2" : "grid-cols-1"} ${MENU_LAYOUT_LIST.length > 2 ? "md:grid-cols-3" : ""} gap-2.5`}>
              {MENU_LAYOUT_LIST.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => setLayout(l.id)}
                  className={`text-left p-3 rounded-xl border transition-colors duration-200 ${
                    layout === l.id ? "border-brand bg-brand/10" : "border-border bg-background hover:border-foreground/40"
                  }`}
                  data-testid={`layout-${l.id}-btn`}
                >
                  <div className={`tile-stamp w-9 h-9 rounded-lg flex items-center justify-center text-base ${
                    layout === l.id ? "bg-brand text-brand-foreground border-brand" : ""
                  }`}>
                    {l.letter}
                  </div>
                  <div className="font-display font-bold text-sm mt-2.5">{l.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 leading-tight">{l.desc}</div>
                </button>
              ))}
            </div>
          </div>

          )}

          <Button
            onClick={RANDOM_MATCHMAKING ? startSearch : () => setPlayersOpen(true)}
            size="lg"
            className="btn-tile w-full md:w-auto h-12 px-8 rounded-xl bg-brand hover:bg-brand-hover text-brand-foreground text-base"
            data-testid="find-match-cta"
          >
            Find opponent
            <ArrowRight size={18} weight="bold" className="ml-1.5" />
          </Button>
          <Button
            asChild
            variant="outline"
            size="lg"
            className="w-full md:w-auto md:ml-3 mt-3 md:mt-0 h-12 px-6 rounded-xl font-display font-extrabold text-base text-opp border-2 border-opp bg-transparent hover:bg-opp/10 hover:text-opp"
            data-testid="lobby-how-to-play"
          >
            <Link to="/how-to-play">
              <PlayCircle size={20} weight="fill" className="mr-1.5" />
              How to play
            </Link>
          </Button>
          {RANDOM_MATCHMAKING && (
          <p className="text-xs text-muted-foreground mt-4" data-testid="find-match-format">
            {matchFormat} · Pairs by closest Elo
          </p>
          )}
          </div>
          </div>
        </div>

        {/* Timed games against people, 90 seconds per move (option C,
            2026-10-22; open to everyone since 2026-10-23). */}
        {user?.timed_test && (
        <div
          className="lg:col-span-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-5 rounded-2xl border-2 border-amber-500/70 bg-amber-500/10"
          data-testid="timed-game-card"
        >
          <div className="flex items-start gap-3 min-w-0">
            <Timer size={26} weight="duotone" className="text-amber-500 flex-none mt-0.5" />
            <div className="min-w-0">
              <div className="font-display font-bold text-lg tracking-tight">Live Game</div>
              <div className="text-sm text-muted-foreground">
                Chess clocks: 7 min, 10 min, or 5 min + 20s a move. Run out of time and you lose.
              </div>
            </div>
          </div>
          <Button
            onClick={() => setTimedOpen(true)}
            disabled={!!pendingMatch}
            variant="outline"
            className="flex-none h-11 px-6 rounded-xl font-display font-extrabold border-2 border-amber-500 text-amber-500 bg-transparent hover:bg-amber-500/10 hover:text-amber-500"
            data-testid="timed-game-btn"
          >
            Challenge
            <ArrowRight size={16} weight="bold" className="ml-1.5" />
          </Button>
        </div>
        )}

        <YourGames className="lg:col-span-2" />

        {/* Daily puzzle: right under Your games on phones; on wide screens
            the row below Your games and Recent battles (Jack, 2026-10-16). */}
        <DailyCard className="lg:col-span-3 lg:order-1" />
        {/* Mini daily puzzle (Jack, 2026-10-26), beside the Daily puzzle. */}
        <MiniCard className="lg:col-span-3 lg:order-1" />

        {/* Play a bot — sits BELOW Find a match, spans the left 2 cols */}
        {showBots && (
        <div ref={botCardRef} className={`lg:col-span-3 lg:order-last bg-surface border rounded-2xl p-8 scroll-mt-4 transition-shadow duration-700 ${botCardGlow ? "border-brand ring-2 ring-brand shadow-lg" : "border-border"}`} data-testid="bot-match-card">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-display text-2xl font-bold tracking-tight">Play a bot</h2>
              <p className="text-xs text-muted-foreground mt-1">
                Nothing to play while you wait? Bots start instantly and don't change your Elo.
              </p>
            </div>
            <Robot size={28} weight="duotone" className="text-brand" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="bots-grid">
            {BOTS.map((b) => {
              const busy = startingBot === b.id;
              const lit = busy || botBoardPick?.id === b.id;
              const suggested = b.id === suggestedBot(user?.elo_rating || 1200).id;
              return (
                <button
                  key={b.id}
                  type="button"
                  disabled={!!startingBot || !!pendingMatch}
                  aria-pressed={lit}
                  onClick={() => { setFirstChoice("random"); setBotBoardPick(b); }}
                  className={`group relative text-left p-4 rounded-xl border bg-gradient-to-br ${b.accent}
                    transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:border-foreground/30 hover:brightness-110
                    active:scale-[0.98] active:brightness-125
                    ${lit ? "border-brand ring-2 ring-brand brightness-125 shadow-lg -translate-y-0.5" : `border-border ${suggested ? "ring-2 ring-brand" : `ring-1 ring-inset ${b.ring}`}`}
                    ${(startingBot && !busy) || pendingMatch ? "opacity-60" : ""} disabled:cursor-not-allowed
                    flex flex-col gap-2 min-h-[150px]`}
                  data-testid={`bot-${b.id}-btn`}
                >
                  <div className="flex items-center justify-between">
                    <BotFace name={b.name} className="w-9 h-9 ring-1 ring-foreground/15" />
                    <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground bg-background/60 backdrop-blur px-1.5 py-0.5 rounded-md">
                      {b.elo} ELO
                    </span>
                  </div>
                  <div className="font-display font-bold tracking-tight leading-tight">{b.name}</div>
                  {suggested && (
                    <div className="text-[10px] uppercase tracking-wider font-bold text-brand" data-testid="bot-suggested">
                      Your level
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground leading-snug flex-1">{b.blurb}</div>
                  <div className={`text-[11px] uppercase tracking-wider font-bold text-brand transition-opacity flex items-center gap-1 ${lit ? "opacity-100" : "opacity-100 md:opacity-0 md:group-hover:opacity-100"}`}>
                    {busy ? "Starting…" : "Play"} <ArrowRight size={12} weight="bold" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        )}

        {/* Recent matches */}
        <div className="bg-surface border border-border rounded-2xl p-6" data-testid="recent-matches-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl font-bold tracking-tight">Recent battles</h3>
            <Trophy size={20} weight="duotone" className="text-brand" />
          </div>
          {matches.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground text-sm">
              <Clock size={32} weight="duotone" className="mx-auto mb-2 opacity-50" />
              No matches yet. Play your first!
            </div>
          ) : (
            <ul className="space-y-2.5">
              {matches.map((m) => {
                const isP1 = m.player1_id === user?.id;
                const opp = isP1 ? m.player2_username : m.player1_username;
                // Per-viewer outcome. An agreed draw shows as "draw";
                // otherwise prefer `winner_id`, then
                // `winner_color`; fall back to legacy flip-by-reporter.
                let displayResult;
                if (m.status !== "completed") {
                  displayResult = "pending";
                } else if (m.win_type === "draw") {
                  displayResult = "draw";
                } else if (m.winner_id) {
                  displayResult = m.winner_id === user?.id ? "win" : "loss";
                } else if (m.winner_color === "A") {
                  displayResult = isP1 ? "win" : "loss";
                } else if (m.winner_color === "B") {
                  displayResult = isP1 ? "loss" : "win";
                } else {
                  // Legacy path — flip P1-perspective `result` if needed.
                  // Treat any leftover "draw" as a pending-state badge so
                  // we never render a Draw badge anywhere in the app.
                  const result = m.result || "pending";
                  const reportedByMe = m.result_reporter === user?.id;
                  let derived = reportedByMe
                    ? result
                    : result === "win"
                    ? "loss"
                    : result === "loss"
                    ? "win"
                    : result;
                  if (derived === "draw") derived = "pending";
                  displayResult = derived;
                }

                const colorMap = {
                  win: "bg-brand/15 text-brand border-brand/30",
                  loss: "bg-opp/15 text-opp border-opp/30",
                  pending: "bg-muted text-muted-foreground border-border",
                  draw: "bg-muted text-foreground border-border",
                };

                return (
                  <li
                    key={m.match_id}
                    // Finished games open turn by turn in the Watch viewer.
                    onClick={m.has_moves ? () => navigate(`/watch/${m.match_id}`, { state: { back: true } }) : undefined}
                    className={`flex items-center justify-between p-3 rounded-xl border border-border bg-background ${
                      m.has_moves ? "cursor-pointer hover:border-foreground/25 transition-colors" : ""}`}
                    data-testid={`recent-match-${m.match_id}`}
                  >
                    <div className="min-w-0 flex items-center gap-2">
                      <Avatar
                        userId={m.is_bot ? null : isP1 ? m.player2_id : m.player1_id}
                        username={opp}
                        bot={m.is_bot}
                        className="w-8 h-8 text-xs"
                        fallbackClassName="bg-muted text-muted-foreground"
                      />
                      <div className="min-w-0">
                        <div className="font-medium text-sm truncate">vs {m.is_bot ? opp : <PlayerLink id={isP1 ? m.player2_id : m.player1_id}>{opp}</PlayerLink>}</div>
                        <div className="text-xs text-muted-foreground">
                          {m.layout ? `${LAYOUTS.find((l) => l.id === m.layout)?.name || m.layout.replace("_", " ")} · ` : ""}
                          {timeAgo(m.completed_at || m.created_at)}
                        </div>
                      </div>
                    </div>
                    <span
                      className={`text-xs uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border ${colorMap[displayResult] || colorMap.pending}`}
                    >
                      {displayResult}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <Dialog open={playersOpen} onOpenChange={setPlayersOpen}>
        <DialogContent className="max-w-lg w-[calc(100vw-2rem)] find-opponent-dialog flex flex-col gap-0 p-4 sm:p-6 rounded-2xl" data-testid="find-opponent-dialog">
          <DialogTitle className="font-display text-2xl font-bold tracking-tight">Find opponent</DialogTitle>
          <DialogDescription className="text-xs mt-1 mb-4">
            Challenge any player. They'll see it in Your games.
          </DialogDescription>
          <ChallengePlayers onSent={() => setPlayersOpen(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={timedOpen} onOpenChange={setTimedOpen}>
        <DialogContent className="max-w-lg w-[calc(100vw-2rem)] find-opponent-dialog flex flex-col gap-0 p-4 sm:p-6 rounded-2xl" data-testid="timed-game-dialog">
          <DialogTitle className="font-display text-2xl font-bold tracking-tight">Live Game</DialogTitle>
          <DialogDescription className="text-xs mt-1 mb-4">
            Chess clocks, run out of time and you lose. Players online now are at the top.
          </DialogDescription>
          <ChallengePlayers timed onSent={() => setTimedOpen(false)} />
        </DialogContent>
      </Dialog>

      <MatchmakingOverlay
        open={searching}
        onCancel={cancelSearch}
        onSwitchToAI={switchToAI}
        waited={waited}
        mode={mode}
        opponent={opponent}
      />

      {/* Bot-match board picker — lets the user choose a layout for THIS match
          without changing the lobby default. Opens when a bot card is clicked. */}
      <Dialog open={!!botBoardPick} onOpenChange={(v) => !v && setBotBoardPick(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto bg-surface border-border" data-testid="bot-board-picker">
          <DialogTitle className="font-display text-2xl font-black tracking-tight">
            Pick your board
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Choose the layout for your match against{" "}
            <span className="text-foreground font-semibold">{botBoardPick?.name}</span>.
          </DialogDescription>
          <UnbrokenChainToggle
            className="mt-3"
            checked={testUnbroken}
            onChange={setTestUnbroken}
            testId="bot-test-unbroken"
          />
          <SameTilesToggle
            className="mt-2"
            checked={sameTiles}
            onChange={setSameTiles}
            testId="bot-same-tiles"
          />
          <div className="mt-3" data-testid="bot-turns-choice">
            <div className="text-sm font-semibold">Turns</div>
            <div className="grid grid-cols-2 gap-1.5 mt-1.5">
              {[[false, "Take turns"], [true, "Same time"]].map(([simul, label]) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => setBotSimul(simul)}
                  className={`rounded-full border py-1.5 text-sm font-semibold transition-colors truncate px-2 ${
                    botSimul === simul ? "border-brand bg-brand/10 text-brand" : "border-border text-muted-foreground hover:border-brand/60"
                  }`}
                  aria-pressed={botSimul === simul}
                  data-testid={`bot-turns-${simul ? "same" : "take"}`}
                >
                  {label}
                </button>
              ))}
            </div>
            {botSimul && <p className="text-xs text-muted-foreground mt-1.5">You and the bot play each turn at once.</p>}
            {botSimul && (
              <div className="mt-3" data-testid="bot-clock-choice">
                <div className="text-sm font-semibold">Clock</div>
                <div className="grid grid-cols-3 gap-1.5 mt-1.5">
                  {CLOCKS.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setBotClock(c.id)}
                      className={`rounded-full border py-1.5 text-sm font-semibold transition-colors truncate px-2 ${
                        botClock === c.id ? "border-brand bg-brand/10 text-brand" : "border-border text-muted-foreground hover:border-brand/60"
                      }`}
                      aria-pressed={botClock === c.id}
                      data-testid={`bot-clock-${c.id}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          {!botSimul && <div className="mt-3" data-testid="bot-first-choice">
            <div className="text-sm font-semibold">Who goes first</div>
            <div className="grid grid-cols-3 gap-1.5 mt-1.5">
              {[["me", "You"], ["bot", botBoardPick?.name?.split(" ").pop() || "Bot"], ["random", "Random"]].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setFirstChoice(id)}
                  className={`rounded-full border py-1.5 text-sm font-semibold transition-colors truncate px-2 ${
                    firstChoice === id ? "border-brand bg-brand/10 text-brand" : "border-border text-muted-foreground hover:border-brand/60"
                  }`}
                  aria-pressed={firstChoice === id}
                  data-testid={`bot-first-${id}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>}
          <div className="mt-2">
            <BoardChoice
              disabled={startingBot === botBoardPick?.id}
              onPick={(id) => botBoardPick && startBotMatch(botBoardPick, { layout: id })}
              testIdPrefix="bot-layout"
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="rounded-full mt-1 self-end"
            onClick={() => setBotBoardPick(null)}
            data-testid="bot-picker-cancel"
          >
            Cancel
          </Button>
        </DialogContent>
      </Dialog>

      <ResignConfirmModal
        open={resignConfirmOpen}
        exiting={false}
        onCancel={() => setResignConfirmOpen(false)}
        onConfirm={resignPendingMatch}
      />

      {/* Abandonment-defeat notification: the user's opponent was credited
          a win because the user (or their connection) dropped out past the
          grace window. They never saw the Game Over modal because they
          weren't in /match/{id} when it happened. Surface it here. */}
      <Dialog open={!!defeatNotice} onOpenChange={(v) => !v && dismissDefeatNotice()}>
        <DialogContent
          className="sm:max-w-md bg-surface border-border"
          data-testid="abandonment-defeat-modal"
        >
          <DialogTitle className="font-display text-2xl font-black tracking-tight text-rose-500">
            Defeat
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Your match against{" "}
            <span className="text-foreground font-semibold">
              {defeatNotice?.oppName}
            </span>{" "}
            {defeatNotice?.idle
              ? "was forfeited. You didn't play your turn for 10 days, so the win went to your opponent."
              : "was forfeited — you were disconnected past the grace window, so the win was awarded to your opponent."}
          </DialogDescription>
          {typeof defeatNotice?.eloDelta === "number" && (
            <div className="text-sm">
              <span className="text-muted-foreground">Elo change: </span>
              <span
                className={
                  defeatNotice.eloDelta >= 0
                    ? "text-emerald-500 font-semibold"
                    : "text-rose-500 font-semibold"
                }
              >
                {defeatNotice.eloDelta >= 0 ? "+" : ""}
                {defeatNotice.eloDelta}
              </span>
            </div>
          )}
          <div className="flex justify-end gap-2 mt-2">
            <Button
              variant="ghost"
              size="sm"
              className="rounded-full"
              onClick={dismissDefeatNotice}
              data-testid="defeat-notice-dismiss"
            >
              Dismiss
            </Button>
            <Button
              size="sm"
              className="rounded-full"
              onClick={() => {
                const mid = defeatNotice?.matchId;
                dismissDefeatNotice();
                if (mid) navigate(`/match/${mid}`);
              }}
              data-testid="defeat-notice-view"
            >
              View match
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <NamePoll blocked={!!defeatNotice || resignConfirmOpen || !!botBoardPick || playersOpen || timedOpen || searching} />
    </main>
  );
}
